import { useAppContext } from "@/appContext";
import Nav from "./nav";
import TrackBreadCrumb from "./TrackBreadCrumb";
import TrackHero from "./TrackHero";
import TrackSession from "./TrackSession";
import { useEffect, useMemo, useState } from "react";
import { ITrackQuestion, ITrackSessionQuestions, Question, SessionQuestion } from "@/utils/types";
import { capitalizeFirstLetter, generateGUID, isQuestionInSelected } from "@/utils/utils";

const TrackDashboard = () => {
  const { state, navigateThenRun, goToStartCont, startFunc, reducers } = useAppContext();
  const { questions } = state;
  const [selectedQuestions, setSelectedQuestions] = useState<Question[]>([]);
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [query, setQuery] = useState("");
  const [difficulty, setDifficulty] = useState<string | null>("all");

  const attemptedQuestions = state.profileUser?.answeredQuestions||{}
  const startSession = () => {
    let sesh: SessionQuestion[] = [];
    let sessionId = generateGUID();
    selectedQuestions.forEach((q) =>
      sesh.push({
        question: q,
        feedback: "",
        actionsteps: "",
        prompt: [],
        fullprompt: [],
        samplesolution: "",
        code: "",
        useWhiteboard: state.useWhiteboard,
        codingLang: state.codingLang,
        time: 0,
        isHelpful: null,
        isHelpfulMessage: null,
        role: state.role,
        feedbackDuration: 0,
        sessionId: sessionId,
      })
    );
    reducers.setsession(sesh);
    startFunc(0);
  };

  const attempted =
    state.profileUser && state.profileUser.answeredQuestions
      ? state.profileUser.answeredQuestions
      : {};

  const mappedQuestions = useMemo(() => {
    if (!questions) return {} as ITrackSessionQuestions;

    //else
    const mapped: ITrackSessionQuestions = {};

    const allQuestions = Object.values(state.questions).flat();
    if (allQuestions.length == 0){
      return {};
    }
    const choosenQuestions: ITrackQuestion[] = allQuestions
      .map((question: Question) => {
        if (
          selectedFilters.length > 0 &&
          !selectedFilters.includes(question.specType)
        )
          return;
        //else
        if (
          query &&
          !question.question.toLowerCase().includes(query.toLowerCase())
        ) {
          return null;
        }

        if (
          difficulty !== "all" &&
          question.level?.toLowerCase() != difficulty
        ) {
          return null;
        }
        const attempts = attemptedQuestions[question.question]?.length||0;
        return {
          question: question,
          selected: isQuestionInSelected(question, selectedQuestions),
          tag: question.specType,
          tagColor: "bg-[#EFF8FF] text-[#6941C6]",
          difficulty: question.level ?? "General",
          noOfStars: attempts > 0 ? attemptedQuestions[question.question][attempts-1].stars||0 : 0
        };
      })
      .filter((q): q is ITrackQuestion => q !== null && q !== undefined);
    mapped["Selected Questions"] = {
      color: "bg-[#ff914d8b]",
      questions: selectedQuestions.map((q) => {
        const attempts = attemptedQuestions[q.question]?.length||0;
        return {
          question: q,
          selected: true,
          tag: q.specType,
          tagColor: "bg-[#EFF8FF] text-[#6941C6]",
          difficulty: q.level ?? "General",
          noOfStars: attempts > 0 ? attemptedQuestions[q.question][attempts-1].stars||0 : 0,
        };
      }),
    };
    mapped["Questions"] = {
      color: "bg-[#F2FDE9]",
      questions: choosenQuestions,
    };
    return mapped;
  }, [questions, selectedQuestions, selectedFilters, query, difficulty]);

  const filterTags = useMemo(() => {
    if (!questions || Object.keys(questions).length == 0) return [];
    return [...new Set(Object.keys(questions))];
  }, [questions]);

  useEffect(() => {
    if (Object.keys(questions).length !== 0) {
      // Initialize array to store selected questions
      const selectedQuestions: Question[] = [];

      // Get all unattempted questions first
      const allUnattemptedQuestions = Object.keys(questions).flatMap((key) =>
        questions[key].filter((q: any) => attempted[q.question] === undefined)
      );

      // If we have at least 3 unattempted questions, randomly select 3
      if (allUnattemptedQuestions.length >= 3) {
        while (selectedQuestions.length < 3) {
          const randomIndex = Math.floor(
            Math.random() * allUnattemptedQuestions.length
          );
          const question = allUnattemptedQuestions[randomIndex];

          // Only add if not already selected
          if (
            !selectedQuestions.some((sq) => sq.question === question.question)
          ) {
            selectedQuestions.push(question);
            allUnattemptedQuestions.splice(randomIndex, 1); // Remove selected question
          }
        }
      } else {
        // Add all available unattempted questions
        selectedQuestions.push(...allUnattemptedQuestions);

        // Only if we have less than 3 unattempted questions, fill with attempted ones
        if (selectedQuestions.length < 3) {
          const allQuestions = Object.values(questions).flat();
          const remainingQuestions = allQuestions.filter(
            (q) => !selectedQuestions.some((sq) => sq.question === q.question)
          );

          while (
            selectedQuestions.length < 3 &&
            remainingQuestions.length > 0
          ) {
            const randomIndex = Math.floor(
              Math.random() * remainingQuestions.length
            );
            selectedQuestions.push(remainingQuestions[randomIndex]);
            remainingQuestions.splice(randomIndex, 1);
          }
        }
      }

      // Update the questions state with our selection
      setSelectedQuestions(selectedQuestions);
    }
  }, [questions]);

  return (
    <>
      <Nav />
      <div className="w-full flex flex-col p-[50px] gap-[100px] overflow-hidden">
        <TrackBreadCrumb
          goToDashboard={() =>
            navigateThenRun(() => {
              goToStartCont();
            }, "/")
          }
        />
        <TrackHero
          track={`${capitalizeFirstLetter(state.role)} Path`}
          trackTime="15 min"
          trackDescription="Take your learning to another level with this holistic package designed to give you a complete understanding of the key concepts. Dive deep into specific topics with in-depth questions for your field."
          readinessLevel={1}
          stars={4}
          questionsPracticed={3}
          totalQuestions={5}
        />
        <TrackSession
          sessionName="Customize Session"
          sessionDescription="We recommend practicing three questions per session, but you can choose as little as one."
          questions={mappedQuestions}
          setSelectedQuestions={setSelectedQuestions}
          filters={filterTags}
          setSelectedFilters={setSelectedFilters}
          selectedFilters={selectedFilters}
          query={query}
          setQuery={setQuery}
          setDifficulty={setDifficulty}
          startSession={startSession}
        />
      </div>
    </>
  );
};

export default TrackDashboard;
